import { useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  ListForecastRunsResponse,
  GetForecastRunResponse,
  ListForecastSeriesInput,
  ListForecastSeriesResponse,
  ForecastRunSummary,
  ForecastRunDivision,
  ForecastSeriesRow,
} from '@erp/rpc';

export type {
  ForecastRunSummary,
  ForecastRunDivision,
  ForecastSeriesRow,
  GetForecastRunResponse,
};

// Recent forecast runs, newest first. Short staleTime so a run kicked off from
// the workflows page shows up without a hard reload.
export function useForecastRuns(limit = 25) {
  return useQuery<ListForecastRunsResponse>({
    ...orpc.procurement.forecastRuns.list.queryOptions({ input: { limit } }),
    staleTime: 30_000,
  });
}

// One run's manifest, per-division coverage and horizon curve. Omit runId for
// the most recent production run that produced output.
export function useForecastRun(runId?: string) {
  return useQuery<GetForecastRunResponse>({
    ...orpc.procurement.forecastRuns.get.queryOptions({ input: { runId } }),
    staleTime: 30_000,
  });
}

// The run's series, paginated and filterable. Previous page data is kept so
// paging and filtering don't blank the table — but only within the SAME run:
// carrying it across a run change would render one run's series under the
// other run's overview.
export function useForecastSeries(input: ListForecastSeriesInput) {
  return useQuery<ListForecastSeriesResponse>({
    ...orpc.procurement.forecastRuns.listSeries.queryOptions({ input }),
    placeholderData: (previous) =>
      previous?.runId === input.runId ? previous : undefined,
    staleTime: 30_000,
  });
}
